import { useMemo, useState } from 'react';
import { useDaten } from '../daten/kontext';
import type { Id, Datum, Einsatz, Kurs, Zeitfenster, Stand } from '../daten/typen';
import { kursFenster, kalenderSperre, sollIst, fachbereichsKnoten, knotenPfad, pruefeEinsatz, fehlerText, dozentenLage, freieRaeume, naechsterOffenerKnoten, planVorschlag, kinderVon } from '../daten/regeln';
import { montag, plusTage, heute, kw, deKurz, tagKurz, kurzeZeit, deLang } from '../daten/zeit';
import { EinsatzPanel, type PanelZiel } from './EinsatzPanel';
import { Knopf, Icon, Kuerzel, Fortschritt, dozentName, Leer } from '../ui/bausteine';

interface Stempel { kursId: Id; fbKnotenId: Id; dozentId: Id | null }

export function Planung({ startWoche, startEinsatz }: { startWoche?: Datum; startEinsatz?: Id }) {
  const { stand, quelle, ausfuehren, melden, fragen } = useDaten();
  const [woche, setWoche] = useState<Datum>(startWoche ?? montag(heute()));
  const [ausgeblendet, setAusgeblendet] = useState<Set<Id>>(new Set());
  const [ziel, setZiel] = useState<PanelZiel | null>(() => {
    const e = startEinsatz ? stand.einsaetze.find(x => x.id === startEinsatz) : undefined;
    return e ? { kursId: e.kurs_id, datum: e.datum, beginn: e.beginn, ende: e.ende, einsatzId: e.id } : null;
  });
  const [fokus, setFokus] = useState<Id | null>(null);
  const [stempel, setStempel] = useState<Stempel | null>(null);
  const [hervor, setHervor] = useState<Id | ''>('');
  const [leisteOffen, setLeisteOffen] = useState(true);

  const tage = useMemo(() => [0, 1, 2, 3, 4].map(i => plusTage(woche, i)), [woche]);
  const freitag = tage[4];
  const kurseWoche = useMemo(() => stand.kurse
    .filter(k => k.status !== 'abgeschlossen' && k.beginn <= freitag && k.ende >= woche)
    .sort((a, b) => a.kennung.localeCompare(b.kennung)), [stand.kurse, woche, freitag]);
  const sichtbar = kurseWoche.filter(k => !ausgeblendet.has(k.id));
  const fokusKurs = stand.kurse.find(k => k.id === (fokus ?? sichtbar[0]?.id));

  const entwuerfe = stand.einsaetze.filter(e => e.status === 'entwurf' && e.datum >= woche && e.datum <= freitag && sichtbar.some(k => k.id === e.kurs_id));
  const offeneAbsagen = stand.absagen.filter(a => !a.erledigt_am).length;

  async function stempelSetzen(kurs: Kurs, datum: Datum, f: Zeitfenster) {
    if (!stempel) return;
    const knoten = naechsterOffenerKnoten(stand, kurs.id, stempel.fbKnotenId) ?? stand.kursKnoten.find(k => k.id === stempel.fbKnotenId)!;
    let dozentId = stempel.dozentId;
    if (dozentId) {
      const l = dozentenLage(stand, kurs.id, knoten.id, datum, f.beginn, f.ende).find(x => x.dozent.id === dozentId);
      if (!l?.frei) { melden(`${l?.dozent.kuerzel ?? 'Dozent'} ist hier nicht frei (${l?.grund ?? ''}) – bitte im Formular wählen.`, 'info');
        setZiel({ kursId: kurs.id, datum, beginn: f.beginn, ende: f.ende, knotenVorschlag: knoten.id }); return; }
    }
    const raum = kurs.standard_form === 'praesenz'
      ? (freieRaeume(stand, kurs.standort_id, datum, f.beginn, f.ende).find(r => r.raum.id === kurs.standard_raum_id && r.frei)
        ?? freieRaeume(stand, kurs.standort_id, datum, f.beginn, f.ende).find(r => r.frei))?.raum.id ?? null : null;
    const e = { kurs_id: kurs.id, kurs_knoten_id: knoten.id, dozent_id: dozentId, raum_id: raum, form: kurs.standard_form,
      datum, beginn: f.beginn, ende: f.ende, ue: f.ue, status: 'entwurf' as const, hinweis: null };
    const v = pruefeEinsatz(stand, e);
    if (v) { melden(fehlerText(v), 'fehler'); return; }
    await ausfuehren(() => quelle.einsatzSpeichern(e));
  }

  async function vorschlag() {
    const ziele = fokus ? sichtbar.filter(k => k.id === fokus) : sichtbar;
    const liste = ziele.flatMap(k => planVorschlag(stand, k.id, woche, freitag));
    if (!liste.length) { melden('In dieser Woche gibt es keine freien Zeitfenster mehr.', 'info'); return; }
    const offen = liste.filter(e => !e.dozent_id).length;
    if (!await fragen(`Planvorschlag: ${liste.length} Entwürfe für ${ziele.length === 1 ? ziele[0].kennung : ziele.length + ' Kurse'} in KW ${kw(woche)} anlegen?${offen ? `\n${offen} davon ohne freien Dozenten.` : ''}\n\nEntwürfe sehen nur Admins, bis sie freigegeben sind.`, 'Entwürfe anlegen')) return;
    let n = 0;
    await ausfuehren(async () => { n = await quelle.einsaetzeAnlegen(liste); });
    melden(`${n} Entwürfe angelegt – bitte prüfen und freigeben.`, 'ok');
  }

  return (
    <div className={`planung ${leisteOffen ? 'mit-leiste' : ''}`}>
      <div className="werkzeugleiste">
        <div className="gruppe">
          <Knopf art="leise" icon="links" aria-label="Vorige Woche" onClick={() => setWoche(plusTage(woche, -7))} />
          <div className="woche"><strong>KW {kw(woche)}</strong><span>{deKurz(woche)} – {deKurz(freitag)}{woche.slice(0, 4)}</span></div>
          <Knopf art="leise" icon="rechts" aria-label="Nächste Woche" onClick={() => setWoche(plusTage(woche, 7))} />
          <Knopf art="leise" onClick={() => setWoche(montag(heute()))}>Heute</Knopf>
        </div>
        <div className="gruppe filter">
          {kurseWoche.map(k => (
            <button key={k.id} type="button" className={`chip kurs-chip ${ausgeblendet.has(k.id) ? '' : 'an'}`} style={{ ['--kursfarbe' as any]: k.farbe }}
              onClick={() => { const n = new Set(ausgeblendet); n.has(k.id) ? n.delete(k.id) : n.add(k.id); setAusgeblendet(n); }}>
              {k.kennung}
            </button>
          ))}
        </div>
        <div className="gruppe rechts">
          <select value={hervor} onChange={e => setHervor(e.target.value)} aria-label="Dozent hervorheben" className="kompakt">
            <option value="">Dozent hervorheben …</option>
            {[...stand.dozenten].sort((a, b) => a.nachname.localeCompare(b.nachname, 'de')).map(d => <option key={d.id} value={d.id}>{d.kuerzel} · {d.nachname}</option>)}
          </select>
          <Knopf icon="zauber" onClick={vorschlag} title="Freie Zeitfenster der Woche automatisch als Entwurf füllen">Vorschlag</Knopf>
          <Knopf art="primaer" icon="senden" disabled={!entwuerfe.length}
            onClick={() => ausfuehren(() => quelle.freigeben(entwuerfe.map(e => e.id)), `${entwuerfe.length} Termine freigegeben`)}>
            Woche freigeben{entwuerfe.length ? ` (${entwuerfe.length})` : ''}
          </Knopf>
          <Knopf art="leise" icon="liste" aria-label="Soll-Ist-Leiste ein- oder ausblenden" onClick={() => setLeisteOffen(!leisteOffen)} />
        </div>
      </div>

      {stempel && (() => {
        const k = stand.kursKnoten.find(x => x.id === stempel.fbKnotenId);
        const d = stand.dozenten.find(x => x.id === stempel.dozentId);
        return <div className="stempel-hinweis"><Icon name="stempel" /> Stempel aktiv: <strong>{k?.bezeichnung}</strong>{d ? <> mit <strong>{dozentName(d)}</strong></> : ' ohne Dozent'} – tippen Sie auf freie Felder von {stand.kurse.find(x => x.id === stempel.kursId)?.kennung}.
          <Knopf art="leise" icon="x" onClick={() => setStempel(null)}>Beenden</Knopf></div>;
      })()}
      {offeneAbsagen > 0 && <div className="absage-hinweis"><Icon name="glocke" /> {offeneAbsagen} offene {offeneAbsagen === 1 ? 'Absage' : 'Absagen'} – Termine ohne Dozent sind rot markiert.</div>}

      <div className="planung-koerper">
        <div className="tafel-rahmen">
          {sichtbar.length === 0 ? <Leer icon="kalender" titel="In dieser Woche läuft kein Kurs" text="Wechseln Sie die Woche oder legen Sie unter „Kurse“ einen neuen Kurs an." /> : (
            <div className="tafel" style={{ gridTemplateColumns: `minmax(170px, 210px) repeat(5, minmax(150px, 1fr))` }}>
              <div className="tafel-ecke" />
              {tage.map(d => {
                const sperre = stand.kalender.find(t => t.datum === d && !t.standort_id);
                return <div key={d} className={`tafel-tag ${d === heute() ? 'heute' : ''}`}><strong>{tagKurz(d)} {deKurz(d)}</strong>{sperre && <span className="sperre-label">{sperre.bezeichnung}</span>}</div>;
              })}
              {sichtbar.map(kurs => (
                <KursZeile key={kurs.id} stand={stand} kurs={kurs} tage={tage} hervor={hervor} fokus={fokusKurs?.id === kurs.id}
                  stempel={stempel?.kursId === kurs.id ? stempel : null}
                  aufKurs={() => { setFokus(kurs.id); setLeisteOffen(true); }}
                  aufLeer={(d, f) => stempel?.kursId === kurs.id ? stempelSetzen(kurs, d, f) : setZiel({ kursId: kurs.id, datum: d, beginn: f.beginn, ende: f.ende })}
                  aufEinsatz={e => setZiel({ kursId: e.kurs_id, datum: e.datum, beginn: e.beginn, ende: e.ende, einsatzId: e.id })} />
              ))}
            </div>
          )}
        </div>
        {leisteOffen && fokusKurs && (
          <SollIstLeiste kurs={fokusKurs} kurse={sichtbar} setFokus={setFokus} stempel={stempel} setStempel={setStempel} />
        )}
      </div>
      <EinsatzPanel ziel={ziel} schliessen={() => setZiel(null)} />
    </div>
  );
}

function KursZeile({ stand, kurs, tage, hervor, fokus, stempel, aufKurs, aufLeer, aufEinsatz }: {
  stand: Stand; kurs: Kurs; tage: Datum[]; hervor: Id | ''; fokus: boolean; stempel: Stempel | null;
  aufKurs: () => void; aufLeer: (d: Datum, f: Zeitfenster) => void; aufEinsatz: (e: Einsatz) => void;
}) {
  const fenster = kursFenster(stand, kurs);
  const si = sollIst(stand, kurs.id);
  const wurzeln = stand.kursKnoten.filter(k => k.kurs_id === kurs.id && !k.parent_id);
  const soll = wurzeln.reduce((s, k) => s + (si.get(k.id)?.soll ?? 0), 0);
  const geplant = wurzeln.reduce((s, k) => s + (si.get(k.id)?.geplant ?? 0), 0);
  const frei = wurzeln.reduce((s, k) => s + (si.get(k.id)?.freigegeben ?? 0), 0);
  const standort = stand.standorte.find(s => s.id === kurs.standort_id);
  const einsaetze = stand.einsaetze.filter(e => e.kurs_id === kurs.id && e.datum >= tage[0] && e.datum <= tage[4]);

  return (
    <>
      <button type="button" className={`kurs-kopf ${fokus ? 'fokus' : ''}`} style={{ ['--kursfarbe' as any]: kurs.farbe }} onClick={aufKurs}>
        <strong>{kurs.kennung}</strong>
        <span className="leise">{standort?.bezeichnung.split(' · ')[0] ?? '–'} · {kurs.standard_form === 'online' ? 'online' : 'Präsenz'}</span>
        <Fortschritt wert={geplant} max={soll} freigegeben={frei} />
        <span className="klein">{geplant} / {soll} UE geplant</span>
      </button>
      {tage.map(d => {
        if (d < kurs.beginn || d > kurs.ende) return <div key={d} className="tafel-zelle ausserhalb"><span>{d < kurs.beginn ? 'vor Kursbeginn' : 'nach Kursende'}</span></div>;
        const sperre = kalenderSperre(stand, d, kurs.standort_id);
        if (sperre) return <div key={d} className="tafel-zelle gesperrt"><span>{sperre.bezeichnung}</span></div>;
        const tagE = einsaetze.filter(e => e.datum === d);
        const belegt = new Set<Id>();
        return (
          <div key={d} className="tafel-zelle">
            {fenster.map(f => {
              const e = tagE.find(x => x.beginn === f.beginn && x.status !== 'entfaellt') ?? tagE.find(x => x.beginn === f.beginn);
              if (e) { belegt.add(e.id); return <Kachel key={f.id} stand={stand} e={e} hervor={hervor} onClick={() => aufEinsatz(e)} />; }
              const blockiert = tagE.some(x => x.status !== 'entfaellt' && x.beginn < f.ende && f.beginn < x.ende);
              if (blockiert) return null;
              return (
                <button key={f.id} type="button" className={`kachel leer ${stempel ? 'stempelbar' : ''}`} onClick={() => aufLeer(d, f)}>
                  <Icon name={stempel ? 'stempel' : 'plus'} /><span>{f.bezeichnung}</span><small>{kurzeZeit(f.beginn)}–{kurzeZeit(f.ende)}</small>
                </button>
              );
            })}
            {tagE.filter(e => !belegt.has(e.id)).map(e => <Kachel key={e.id} stand={stand} e={e} hervor={hervor} abweichend onClick={() => aufEinsatz(e)} />)}
          </div>
        );
      })}
    </>
  );
}

function Kachel({ stand, e, hervor, abweichend, onClick }: { stand: Stand; e: Einsatz; hervor: Id | ''; abweichend?: boolean; onClick: () => void }) {
  const fbK = fachbereichsKnoten(stand, e.kurs_knoten_id);
  const fb = stand.fachbereiche.find(f => f.id === fbK?.fachbereich_id);
  const pfad = knotenPfad(stand, e.kurs_knoten_id);
  const thema = pfad.length ? pfad.slice(Math.max(1, pfad.length - 2)).map(k => k.bezeichnung).join(' · ') : 'ohne Thema';
  const d = stand.dozenten.find(x => x.id === e.dozent_id);
  const raum = stand.raeume.find(r => r.id === e.raum_id);
  const offen = !e.dozent_id && e.status !== 'entfaellt';
  const gedimmt = hervor && e.dozent_id !== hervor;
  return (
    <button type="button" onClick={onClick}
      className={`kachel ${e.status} ${offen ? 'offen' : ''} ${gedimmt ? 'gedimmt' : ''} ${hervor && e.dozent_id === hervor ? 'hervor' : ''}`}
      style={{ ['--fbfarbe' as any]: fb?.farbe ?? 'var(--mm-linie)' }}
      aria-label={`${thema}, ${d ? dozentName(d) : 'ohne Dozent'}, ${kurzeZeit(e.beginn)} bis ${kurzeZeit(e.ende)}, ${e.status}`}>
      <span className="kachel-kopf">
        {fb && <span className="fb-marke">{fb.kuerzel}</span>}
        <span className="kachel-thema">{thema}</span>
      </span>
      <span className="kachel-dozent">
        {d ? <><Kuerzel text={d.kuerzel} farbe={d.farbe} klein /><span>{d.nachname}</span></>
           : e.status === 'entfaellt' ? <span>entfällt</span> : <span className="warn-text"><Icon name="warnung" groesse={14} /> Vertretung gesucht</span>}
      </span>
      <span className="kachel-fuss">
        <span>{abweichend || e.status === 'entfaellt' ? `${kurzeZeit(e.beginn)}–${kurzeZeit(e.ende)}` : ''}</span>
        <span className="kachel-ort">{e.form === 'online' ? <><Icon name="online" groesse={13} /> online</> : raum ? <><Icon name="raum" groesse={13} /> {raum.bezeichnung}</> : null}</span>
        {e.status === 'entwurf' && <span className="tag">Entwurf</span>}
      </span>
      {e.hinweis && <span className="kachel-hinweis">{e.hinweis}</span>}
    </button>
  );
}

function SollIstLeiste({ kurs, kurse, setFokus, stempel, setStempel }: {
  kurs: Kurs; kurse: Kurs[]; setFokus: (id: Id) => void; stempel: Stempel | null; setStempel: (s: Stempel | null) => void;
}) {
  const { stand, quelle, ausfuehren } = useDaten();
  const si = sollIst(stand, kurs.id);
  const knoten = stand.kursKnoten.filter(k => k.kurs_id === kurs.id);
  const kinder = kinderVon(knoten);
  const fbWurzeln = knoten.filter(k => k.fachbereich_id && !knoten.some(p => p.id === k.parent_id && p.fachbereich_id));
  const [auf, setAuf] = useState<Set<Id>>(new Set());

  return (
    <aside className="leiste" aria-label="Soll und Ist">
      <div className="leiste-kopf">
        <select value={kurs.id} onChange={e => setFokus(e.target.value)} aria-label="Kurs für Soll und Ist">
          {kurse.map(k => <option key={k.id} value={k.id}>{k.kennung}</option>)}
        </select>
        <span className="leise klein">{deLang(kurs.beginn)} – {deLang(kurs.ende)}</span>
      </div>
      {[...kinder.get(null) ?? []].map(teil => {
        const w = si.get(teil.id)!;
        return (
          <div key={teil.id} className="leiste-teil">
            {!teil.fachbereich_id && <>
              <div className="leiste-zeile"><strong>{teil.bezeichnung}</strong><span>{w.geplant}/{w.soll}</span></div>
              <Fortschritt wert={w.geplant} max={w.soll} freigegeben={w.freigegeben} />
            </>}
            {fbWurzeln.filter(f => f.id === teil.id || knotenPfad(stand, f.id).some(p => p.id === teil.id)).map(fbk => {
              const fb = stand.fachbereiche.find(x => x.id === fbk.fachbereich_id)!;
              const fw = si.get(fbk.id)!;
              const aktiv = stempel?.fbKnotenId === fbk.id;
              const qualifizierte = stand.dozenten.filter(d => d.aktiv && stand.qualifikationen.some(q => q.dozent_id === d.id && q.fachbereich_id === fb.id));
              return (
                <div key={fbk.id} className={`leiste-fb ${aktiv ? 'aktiv' : ''}`} style={{ ['--fbfarbe' as any]: fb.farbe }}>
                  <div className="leiste-zeile">
                    <button type="button" className="link" onClick={() => { const n = new Set(auf); n.has(fbk.id) ? n.delete(fbk.id) : n.add(fbk.id); setAuf(n); }}>
                      <Icon name={auf.has(fbk.id) ? 'links' : 'rechts'} groesse={14} /> {fbk.bezeichnung}
                    </button>
                    <span>{fw.geplant}/{fw.soll}</span>
                  </div>
                  <Fortschritt wert={fw.geplant} max={fw.soll} freigegeben={fw.freigegeben} />
                  <div className="leiste-zeile">
                    <select className="kompakt" value={fbk.standard_dozent_id ?? ''} aria-label={`Standard-Dozent ${fbk.bezeichnung}`}
                      onChange={e => ausfuehren(() => quelle.speichern('kurs_knoten', { id: fbk.id, standard_dozent_id: e.target.value || null }), 'Standard-Dozent gespeichert')}>
                      <option value="">Standard-Dozent wählen …</option>
                      {qualifizierte.map(d => <option key={d.id} value={d.id}>{d.kuerzel} · {dozentName(d)}</option>)}
                    </select>
                    <Knopf art={aktiv ? 'primaer' : 'sekundaer'} icon="stempel" title="Stempel: freie Felder dieses Kurses mit dem nächsten offenen Thema füllen"
                      onClick={() => setStempel(aktiv ? null : { kursId: kurs.id, fbKnotenId: fbk.id, dozentId: fbk.standard_dozent_id })}>
                      {aktiv ? 'aktiv' : ''}
                    </Knopf>
                  </div>
                  {aktiv && (
                    <select className="kompakt" value={stempel?.dozentId ?? ''} aria-label="Dozent für den Stempel"
                      onChange={e => setStempel({ ...stempel!, dozentId: e.target.value || null })}>
                      <option value="">Stempel ohne Dozent</option>
                      {qualifizierte.map(d => <option key={d.id} value={d.id}>Stempel mit {d.kuerzel} · {d.nachname}</option>)}
                    </select>
                  )}
                  {auf.has(fbk.id) && <UnterBaum stand={stand} id={fbk.id} kinder={kinder} si={si} tiefe={0} />}
                </div>
              );
            })}
          </div>
        );
      })}
      <p className="leise klein legende"><span className="lg geplant" /> geplant <span className="lg frei" /> davon freigegeben</p>
    </aside>
  );
}

function UnterBaum({ stand, id, kinder, si, tiefe }: { stand: Stand; id: Id; kinder: Map<Id | null, any[]>; si: Map<Id, { soll: number; geplant: number; freigegeben: number }>; tiefe: number }) {
  return (
    <ul className="baum">
      {(kinder.get(id) ?? []).map(k => {
        const w = si.get(k.id)!;
        const fertig = w.geplant >= w.soll && w.soll > 0;
        return (
          <li key={k.id} style={{ paddingLeft: tiefe * 12 }}>
            <div className="leiste-zeile klein"><span className={fertig ? 'fertig' : ''}>{fertig && <Icon name="haken" groesse={12} />} {k.bezeichnung}</span><span>{w.geplant}/{w.soll}</span></div>
            {kinder.has(k.id) && <UnterBaum stand={stand} id={k.id} kinder={kinder} si={si} tiefe={tiefe + 1} />}
          </li>
        );
      })}
    </ul>
  );
}
